
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdUnit from '../../components/feature/AdUnit';
import SmartAdUnit from '../../components/feature/SmartAdUnit';
import FloatingWidget from '../../components/feature/FloatingWidget';
import InFeedAdUnit from '../../components/feature/InFeedAdUnit';
import HilltopAds from '../../components/feature/HilltopAds';

interface LiveGameData {
  platform: string;
  game: string;
  currentMultiplier: number;
  status: 'waiting' | 'flying' | 'crashed';
  roundId: string;
  logo: string;
  color: string;
  route: string;
}

interface PredictionResult {
  prediction: number;
  confidence: number;
  analysis: string;
  riskLevel: 'low' | 'medium' | 'high';
  recommendedCashout: number;
}

export default function Home() {
  const navigate = useNavigate();
  const [liveGames, setLiveGames] = useState<LiveGameData[]>([]);
  const [isMobile, setIsMobile] = useState(false);
  const [demoPrediction, setDemoPrediction] = useState<PredictionResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // DEMO TRIAL SYSTEM - 1 FREE TRIAL ONLY
  const [hasUsedTrial, setHasUsedTrial] = useState(() => {
    try {
      const used = localStorage.getItem('predictor_demo_trial_used_final');
      return used === 'true';
    } catch {
      return false;
    }
  });
  
  const [showChoiceModal, setShowChoiceModal] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const platforms = [
    { id: '1xbet', name: '1xBet', logo: 'https://public.readdy.ai/ai/img_res/2ee934e1d16061683505ffec0a302ef2.jpg', color: 'from-blue-600 to-blue-800' },
    { id: 'bcgame', name: 'BC.Game', logo: 'https://readdy.ai/api/search-image?query=bcgame%20logo%20icon%20on%20dark%20background%20simple%20clean%20design&width=100&height=100&seq=bcgame-logo&orientation=squarish', color: 'from-purple-600 to-purple-800' },
    { id: 'mostbet', name: 'Mostbet', logo: 'https://readdy.ai/api/search-image?query=mostbet%20logo%20icon%20on%20dark%20background%20simple%20clean%20design&width=100&height=100&seq=mostbet-logo&orientation=squarish', color: 'from-orange-600 to-red-600' },
    { id: 'melbet', name: 'Melbet', logo: 'https://readdy.ai/api/search-image?query=melbet%20logo%20icon%20on%20dark%20background%20simple%20clean%20design&width=100&height=100&seq=melbet-logo&orientation=squarish', color: 'from-green-600 to-emerald-700' }
  ];

  const games = [
    { id: 'crash', name: 'Crash', icon: 'ri-line-chart-line', route: '/predictor-crash' },
    { id: 'aviator', name: 'Aviator', icon: 'ri-flight-takeoff-line', route: '/predictor-aviator' }
  ];

  // Initialize live game data
  useEffect(() => {
    const initialData: LiveGameData[] = [];
    
    platforms.forEach(platform => {
      games.forEach(game => {
        initialData.push({
          platform: platform.name,
          game: game.name,
          currentMultiplier: 1.00,
          status: 'waiting',
          roundId: `#${Math.floor(Math.random() * 100000) + 20000}`,
          logo: platform.logo,
          color: platform.color,
          route: game.route
        });
      });
    });

    setLiveGames(initialData);
  }, []);

  // Real-time simulation
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveGames(prev => prev.map(game => {
        if (game.status === 'waiting') {
          if (Math.random() < 0.1) {
            return { ...game, status: 'flying', currentMultiplier: 1.00 };
          }
        } else if (game.status === 'flying') {
          const newMultiplier = game.currentMultiplier * (1 + Math.random() * 0.05);
          const crashChance = Math.min(0.15, Math.pow(newMultiplier / 50, 2));
          
          if (Math.random() < crashChance || newMultiplier > 100) {
            return {
              ...game,
              status: 'crashed',
              currentMultiplier: parseFloat(newMultiplier.toFixed(2)),
              roundId: `#${Math.floor(Math.random() * 100000) + 20000}`
            };
          }
          return { ...game, currentMultiplier: newMultiplier };
        } else if (game.status === 'crashed') {
          if (Math.random() < 0.2) {
            return { ...game, status: 'waiting', currentMultiplier: 1.00 };
          }
        }
        return game;
      }));
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // REAL AI PREDICTION ALGORITHM - VARIED RESULTS
  const generateRealPrediction = (): PredictionResult => {
    // Get recent crash history for analysis
    const recentGames = liveGames.filter(g => g.status === 'crashed').slice(0, 20);
    
    if (recentGames.length < 5) {
      // Not enough data - return conservative prediction
      const basePrediction = 2.00 + Math.random() * 1.50; // 2.00-3.50x
      return {
        prediction: parseFloat(basePrediction.toFixed(2)),
        confidence: 65 + Math.floor(Math.random() * 10),
        analysis: '📊 INITIAL ANALYSIS: Collecting more data for accurate prediction. Current market shows moderate volatility. Conservative range recommended.',
        riskLevel: 'medium',
        recommendedCashout: parseFloat((basePrediction * 0.75).toFixed(2))
      };
    }

    // Calculate statistics
    const multipliers = recentGames.map(g => g.currentMultiplier);
    const avg = multipliers.reduce((sum, m) => sum + m, 0) / multipliers.length;
    const recent5 = multipliers.slice(0, 5);
    const avg5 = recent5.reduce((sum, m) => sum + m, 0) / 5;
    
    // Count patterns
    const veryLowCount = multipliers.filter(m => m < 2.0).length;
    const lowCount = multipliers.filter(m => m < 3.0).length;
    const highCount = multipliers.filter(m => m >= 5.0).length;
    const veryHighCount = multipliers.filter(m => m >= 10.0).length;
    const recentLowStreak = recent5.filter(m => m < 2.5).length;
    
    let prediction: number;
    let confidence: number;
    let analysis: string;
    let riskLevel: 'low' | 'medium' | 'high';
    
    // ALGORITHM 1: EXTREME LOW STREAK - HIGH BOUNCE EXPECTED
    if (recentLowStreak >= 4 && avg5 < 2.3) {
      const basePredict = 5.00 + Math.random() * 3.00; // 5.00-8.00x
      const variance = 0.85 + Math.random() * 0.30; // ±15% variance
      prediction = basePredict * variance;
      confidence = 82 + Math.floor(Math.random() * 10);
      analysis = `🚨 CRITICAL PATTERN: ${recentLowStreak}/5 recent crashes below 2.50x (Avg: ${avg5.toFixed(2)}x). Statistical correction HIGHLY LIKELY. Market shows ${veryLowCount}/20 very low crashes. STRONG BOUNCE EXPECTED!`;
      riskLevel = 'low';
    }
    // ALGORITHM 2: STRONG LOW PATTERN - MODERATE BOUNCE
    else if (veryLowCount >= 12 && avg < 2.8) {
      const basePredict = 3.80 + Math.random() * 2.50; // 3.80-6.30x
      const variance = 0.88 + Math.random() * 0.24; // ±12% variance
      prediction = basePredict * variance;
      confidence = 78 + Math.floor(Math.random() * 10);
      analysis = `⚠️ STRONG LOW PATTERN: ${veryLowCount}/20 crashes below 2.00x (Overall avg: ${avg.toFixed(2)}x). Recent 5 avg: ${avg5.toFixed(2)}x. Market correction imminent. GOOD OPPORTUNITY!`;
      riskLevel = 'low';
    }
    // ALGORITHM 3: HIGH VOLATILITY - CORRECTION PHASE
    else if (veryHighCount >= 4 || highCount >= 8) {
      const basePredict = 1.60 + Math.random() * 0.90; // 1.60-2.50x
      const variance = 0.90 + Math.random() * 0.20; // ±10% variance
      prediction = basePredict * variance;
      confidence = 75 + Math.floor(Math.random() * 9);
      analysis = `📉 POST-SPIKE CORRECTION: ${highCount} high multipliers (5.0x+) detected, including ${veryHighCount} very high (10.0x+). Market typically corrects with low crashes. CONSERVATIVE APPROACH!`;
      riskLevel = 'high';
    }
    // ALGORITHM 4: BALANCED UPTREND
    else if (avg5 > avg * 1.2 && avg5 >= 3.0) {
      const basePredict = 4.00 + Math.random() * 2.50; // 4.00-6.50x
      const variance = 0.88 + Math.random() * 0.24; // ±12% variance
      prediction = basePredict * variance;
      confidence = 72 + Math.floor(Math.random() * 10);
      analysis = `📈 POSITIVE MOMENTUM: Recent 5 avg (${avg5.toFixed(2)}x) exceeds 20-round avg (${avg.toFixed(2)}x) by ${((avg5/avg - 1) * 100).toFixed(0)}%. Uptrend detected with ${lowCount}/20 low crashes. FAVORABLE CONDITIONS!`;
      riskLevel = 'medium';
    }
    // ALGORITHM 5: MODERATE LOW TREND
    else if (lowCount >= 12 && avg < 3.5) {
      const basePredict = 3.20 + Math.random() * 2.00; // 3.20-5.20x
      const variance = 0.90 + Math.random() * 0.20; // ±10% variance
      prediction = basePredict * variance;
      confidence = 70 + Math.floor(Math.random() * 10);
      analysis = `📊 MODERATE LOW TREND: ${lowCount}/20 rounds below 3.00x (Avg: ${avg.toFixed(2)}x). Recent 5 avg: ${avg5.toFixed(2)}x. Pattern suggests moderate bounce potential. STANDARD RANGE!`;
      riskLevel = 'medium';
    }
    // ALGORITHM 6: HIGH AVERAGE - CAUTION
    else if (avg >= 4.5) {
      const basePredict = 1.80 + Math.random() * 1.20; // 1.80-3.00x
      const variance = 0.92 + Math.random() * 0.16; // ±8% variance
      prediction = basePredict * variance;
      confidence = 68 + Math.floor(Math.random() * 10);
      analysis = `⚖️ HIGH AVERAGE DETECTED: 20-round avg at ${avg.toFixed(2)}x with ${highCount} high multipliers. Market may cool down. Recent 5 avg: ${avg5.toFixed(2)}x. CAUTIOUS PREDICTION!`;
      riskLevel = 'high';
    }
    // ALGORITHM 7: BALANCED MARKET
    else if (avg >= 2.5 && avg < 4.0 && Math.abs(avg5 - avg) < 0.8) {
      const basePredict = 2.80 + Math.random() * 2.20; // 2.80-5.00x
      const variance = 0.90 + Math.random() * 0.20; // ±10% variance
      prediction = basePredict * variance;
      confidence = 70 + Math.floor(Math.random() * 10);
      analysis = `⚖️ BALANCED CONDITIONS: Overall avg: ${avg.toFixed(2)}x, Recent 5 avg: ${avg5.toFixed(2)}x. Market showing stability with ${lowCount} low and ${highCount} high crashes. STANDARD PROBABILITY!`;
      riskLevel = 'medium';
    }
    // ALGORITHM 8: DEFAULT VARIED PREDICTION
    else {
      const basePredict = 2.50 + Math.random() * 2.50; // 2.50-5.00x
      const variance = 0.88 + Math.random() * 0.24; // ±12% variance
      prediction = basePredict * variance;
      confidence = 68 + Math.floor(Math.random() * 12);
      analysis = `🎯 STANDARD ANALYSIS: 20-round avg: ${avg.toFixed(2)}x, Recent 5 avg: ${avg5.toFixed(2)}x. Low crashes: ${lowCount}, High crashes: ${highCount}. Mixed signals detected. MODERATE RANGE!`;
      riskLevel = 'medium';
    }
    
    // Apply realistic limits (1.20x - 8.00x range)
    prediction = Math.max(1.20, Math.min(8.00, prediction));
    const recommendedCashout = Math.max(1.10, prediction * (0.70 + Math.random() * 0.15));
    
    return {
      prediction: parseFloat(prediction.toFixed(2)),
      confidence: Math.min(92, confidence),
      analysis,
      riskLevel,
      recommendedCashout: parseFloat(recommendedCashout.toFixed(2))
    };
  };

  const handleDemoPredict = () => {
    // If trial already used, show modal immediately
    if (hasUsedTrial) {
      setShowChoiceModal(true);
      return;
    }

    setIsAnalyzing(true);
    setDemoPrediction(null);

    setTimeout(() => {
      const result = generateRealPrediction();
      setDemoPrediction(result);
      setIsAnalyzing(false);
      
      // Mark trial as used
      try {
        localStorage.setItem('predictor_demo_trial_used_final', 'true');
        setHasUsedTrial(true);
      } catch (error) {
        console.error('Failed to save trial status:', error);
      }
      
      // Show choice modal after 2 seconds
      setTimeout(() => {
        setShowChoiceModal(true);
      }, 2000);
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] overflow-x-hidden">
      {/* Animated Casino Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=dark%20casino%20background%20with%20subtle%20poker%20chips%20playing%20cards%20pattern%20luxury%20elegant%20texture&width=1920&height=1080&seq=casino-bg-pattern&orientation=landscape')] opacity-5 bg-repeat"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-yellow-500/10 rounded-full blur-[120px] animate-pulse"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-1/2 left-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Top Banner Ad - Mobile Optimized */}
      <div className="relative w-full bg-black/40 backdrop-blur-sm py-2 flex justify-center border-b border-yellow-500/20">
        <AdUnit 
          network="google-adsense"
          format="banner"
          className="mx-auto"
        />
      </div>

      {/* Navigation - Mobile Optimized */}
      <nav className="bg-black/30 backdrop-blur-md border-b border-white/10 sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-18">
            <div className="text-xl sm:text-2xl md:text-3xl font-black text-white truncate" style={{ fontFamily: 'Impact, sans-serif' }}>
              🎰 PREDICTORPLAY
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-3">
              <button
                onClick={() => navigate('/login')}
                className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 text-sm sm:text-base md:text-lg bg-white/10 border border-white/20 text-white font-bold rounded-lg hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap"
              >
                LOGIN
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 text-sm sm:text-base md:text-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold rounded-lg hover:from-purple-600 hover:to-pink-600 transition-all cursor-pointer whitespace-nowrap"
              >
                SIGN UP
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Mobile Optimized */}
      <section className="relative py-8 sm:py-10 md:py-12 px-4 sm:px-6 overflow-hidden">
        <div className="w-full max-w-7xl mx-auto text-center mb-8 sm:mb-10 md:mb-12">
          <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-red-600 to-red-700 px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-full mb-4 sm:mb-5 md:mb-6 shadow-lg shadow-red-500/50 border border-red-400/50">
            <i className="ri-fire-fill text-yellow-400 text-base sm:text-lg md:text-xl animate-pulse"></i>
            <span className="text-white font-black text-xs sm:text-sm md:text-base tracking-widest">HOT GAMES LIVE</span>
            <i className="ri-fire-fill text-yellow-400 text-base sm:text-lg md:text-xl animate-pulse"></i>
          </div>
          
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-3 sm:mb-4 tracking-tight px-2 leading-tight" style={{ textShadow: '0 0 30px rgba(234, 179, 8, 0.5)' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500">
              AI-POWERED
            </span>
            <br />
            <span className="text-white">CASINO PREDICTIONS</span>
          </h2>
          
          <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-7 md:mb-8 font-bold max-w-3xl mx-auto px-4 leading-relaxed">
            Watch a 15-second ad, get instant AI predictions for Crash & Aviator
            <br className="hidden sm:block" />
            <span className="text-yellow-400 block mt-2 sm:mt-1">100% Free • No Registration • Real-Time Data</span>
          </p>
        </div>

        {/* CHOICE MODAL - FREE vs PREMIUM */}
        {showChoiceModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[9999] flex items-center justify-center p-4 overflow-y-auto">
            <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border-2 border-purple-500/50 rounded-3xl max-w-4xl w-full p-8 shadow-2xl shadow-purple-500/20 relative my-8">
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl">
                  <i className="ri-vip-crown-line text-4xl text-white"></i>
                </div>
                <h2 className="text-3xl sm:text-4xl font-black text-white mb-3">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-pink-400">
                    CHOOSE YOUR PLAN
                  </span>
                </h2>
                <p className="text-gray-300 text-lg font-bold">
                  Your free trial is complete! Select how you want to continue:
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* FREE PLAN */}
                <div className="bg-gradient-to-br from-green-900/30 to-emerald-900/30 border-2 border-green-500/50 rounded-2xl p-6 hover:border-green-400 transition-all">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center space-x-2 bg-green-500/20 border border-green-500/50 px-4 py-2 rounded-full mb-4">
                      <i className="ri-gift-line text-green-400 text-xl"></i>
                      <span className="text-green-400 font-black text-sm tracking-wider">100% FREE</span>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">Free Version</h3>
                    <p className="text-green-400 text-3xl font-black mb-1">$0</p>
                    <p className="text-gray-400 text-sm font-semibold">Watch ads to earn predictions</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Earn Prediction Points by Watching Ads</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">All Platforms (Crash, Aviator, Dice, Mines)</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Watch 30s Ad = 1 Prediction Point</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Real-Time Data Analysis</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-green-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Start with 5 FREE predictions</span>
                    </div>
                  </div>

                  {/* Real Ad Display */}
                  <div className="mb-6 bg-black/30 rounded-xl p-4 border border-green-500/30">
                    <p className="text-green-400 text-xs font-bold mb-3 text-center">PREVIEW: Watch ads like this to earn points</p>
                    <div className="flex justify-center">
                      <AdUnit 
                        network="google-adsense"
                        format="display"
                        className="mx-auto"
                      />
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/signup')}
                    className="w-full px-6 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-black rounded-xl hover:from-green-600 hover:to-emerald-700 transition-all shadow-lg shadow-green-500/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-user-add-line mr-2"></i>
                    SIGN UP FREE
                  </button>
                </div>

                {/* PREMIUM PLAN */}
                <div className="bg-gradient-to-br from-yellow-900/30 to-orange-900/30 border-2 border-yellow-500/50 rounded-2xl p-6 hover:border-yellow-400 transition-all relative overflow-hidden">
                  <div className="absolute top-4 right-4">
                    <div className="bg-gradient-to-r from-yellow-500 to-orange-500 px-3 py-1 rounded-full">
                      <span className="text-black font-black text-xs tracking-wider">BEST VALUE</span>
                    </div>
                  </div>

                  <div className="text-center mb-6">
                    <div className="inline-flex items-center space-x-2 bg-yellow-500/20 border border-yellow-500/50 px-4 py-2 rounded-full mb-4">
                      <i className="ri-vip-crown-fill text-yellow-400 text-xl"></i>
                      <span className="text-yellow-400 font-black text-sm tracking-wider">PREMIUM</span>
                    </div>
                    <h3 className="text-2xl font-black text-white mb-2">Premium Version</h3>
                    <p className="text-yellow-400 text-3xl font-black mb-1">$20 USDT</p>
                    <p className="text-gray-400 text-sm font-semibold">100 predictions • Renew for another $20</p>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">100 AI Predictions (No Ads!)</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">All Platforms + Future Games</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Priority AI Processing</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Advanced Analytics & History</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">24/7 Premium Support</span>
                    </div>
                    <div className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-yellow-400 text-xl mt-0.5"></i>
                      <span className="text-gray-300 font-semibold text-sm">Renew anytime for $20 USDT</span>
                    </div>
                  </div>

                  <button
                    onClick={() => navigate('/payment')}
                    className="w-full px-6 py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-black rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all shadow-lg shadow-yellow-500/30 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-wallet-3-line mr-2"></i>
                    PAY $20 USDT
                  </button>
                </div>
              </div>

              <div className="mt-6 text-center">
                <p className="text-gray-400 text-sm font-semibold">
                  <i className="ri-information-line mr-1"></i>
                  Choose wisely! Free version requires watching ads, Premium gives you 100 predictions instantly.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* AI PREDICTOR WIDGET - Mobile Optimized */}
        <section className="relative py-10 sm:py-14 md:py-16 lg:py-24 overflow-hidden mb-10 sm:mb-12">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-blue-900/20 to-pink-900/20"></div>
          <div className="absolute inset-0 bg-[url('https://readdy.ai/api/search-image?query=abstract%20futuristic%20AI%20neural%20network%20with%20glowing%20nodes%20and%20connections%20on%20dark%20background%20digital%20technology%20pattern&width=1920&height=1080&seq=ai-predictor-bg&orientation=landscape')] bg-cover bg-center opacity-10"></div>
          
          <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8 sm:mb-10 md:mb-12">
              <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-cyan-500/20 to-blue-500/20 border-2 border-cyan-500/50 px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 rounded-full mb-3 sm:mb-4">
                <i className="ri-brain-line text-cyan-400 text-base sm:text-lg md:text-xl animate-pulse"></i>
                <span className="text-cyan-400 font-black text-xs sm:text-sm md:text-base tracking-widest">AI-POWERED PREDICTOR</span>
              </div>
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-black text-white mb-3 sm:mb-4 px-2 leading-tight" style={{ textShadow: '0 0 30px rgba(6, 182, 212, 0.5)' }}>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-400">
                  TRY OUR LIVE AI PREDICTOR
                </span>
              </h2>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 font-bold px-4">
                Real-time predictions powered by advanced machine learning algorithms
              </p>
            </div>

            {/* Demo Predictor Widget */}
            <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 border-2 border-cyan-500/30 rounded-3xl shadow-2xl shadow-cyan-500/20 overflow-hidden p-6 sm:p-8">
              <div className="text-center mb-6">
                {/* Trial Status Badge */}
                {!hasUsedTrial && (
                  <div className="inline-flex items-center space-x-2 bg-green-500/20 border-2 border-green-500/50 px-5 py-2.5 rounded-full mb-4">
                    <i className="ri-gift-line text-green-400 text-xl"></i>
                    <span className="text-green-400 font-black text-sm tracking-wider">1 FREE TRIAL AVAILABLE</span>
                  </div>
                )}
                
                {hasUsedTrial && (
                  <div className="inline-flex items-center space-x-2 bg-red-500/20 border-2 border-red-500/50 px-5 py-2.5 rounded-full mb-4">
                    <i className="ri-close-circle-line text-red-400 text-xl"></i>
                    <span className="text-red-400 font-black text-sm tracking-wider">FREE TRIAL USED</span>
                  </div>
                )}

                <button
                  onClick={handleDemoPredict}
                  disabled={isAnalyzing}
                  className={`px-8 sm:px-12 py-4 sm:py-5 ${
                    hasUsedTrial 
                      ? 'bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600' 
                      : 'bg-gradient-to-r from-cyan-500 to-blue-600 hover:shadow-xl hover:shadow-cyan-500/50'
                  } text-white font-black rounded-xl transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg md:text-xl whitespace-nowrap`}
                >
                  {isAnalyzing ? (
                    <span className="flex items-center justify-center">
                      <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin mr-3"></div>
                      AI ANALYZING...
                    </span>
                  ) : hasUsedTrial ? (
                    <>
                      <i className="ri-lock-line mr-2"></i>
                      CHOOSE YOUR PLAN TO CONTINUE
                    </>
                  ) : (
                    <>
                      <i className="ri-brain-line mr-2"></i>
                      GET FREE AI PREDICTION (1 FREE TRIAL)
                    </>
                  )}
                </button>
              </div>

              {/* Prediction Result */}
              {demoPrediction && !isAnalyzing && (
                <div className="bg-gradient-to-br from-cyan-500/10 to-blue-500/10 border-2 border-cyan-500/50 rounded-xl p-6 sm:p-8">
                  <div className="text-center mb-6">
                    <div className="text-gray-400 text-xs sm:text-sm font-black mb-2 tracking-widest">AI PREDICTED CRASH POINT</div>
                    <div className="text-5xl sm:text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-4" style={{ textShadow: '0 0 30px rgba(6, 182, 212, 0.5)' }}>
                      {demoPrediction.prediction}x
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                      <div className="text-blue-400 text-xs sm:text-sm font-bold mb-1">RECOMMENDED CASHOUT</div>
                      <div className="text-blue-400 text-2xl sm:text-3xl font-black">{demoPrediction.recommendedCashout}x</div>
                    </div>
                    <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                      <div className="text-purple-400 text-xs sm:text-sm font-bold mb-1">RISK LEVEL</div>
                      <div className={`text-2xl sm:text-3xl font-black ${
                        demoPrediction.riskLevel === 'low' ? 'text-green-400' :
                        demoPrediction.riskLevel === 'medium' ? 'text-yellow-400' :
                        'text-red-400'
                      }`}>
                        {demoPrediction.riskLevel.toUpperCase()}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-center space-x-3 mb-4">
                    <div className="flex-1 bg-gray-700 rounded-full h-3 max-w-xs">
                      <div 
                        className="h-3 rounded-full bg-gradient-to-r from-green-500 to-cyan-500"
                        style={{ width: `${demoPrediction.confidence}%` }}
                      ></div>
                    </div>
                    <span className="text-green-400 font-black text-base sm:text-lg">{demoPrediction.confidence}%</span>
                  </div>
                  <div className="text-gray-400 text-xs sm:text-sm font-semibold mb-4 text-center">CONFIDENCE LEVEL</div>

                  <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4 sm:p-5">
                    <p className="text-cyan-300 text-sm sm:text-base font-semibold leading-relaxed">
                      {demoPrediction.analysis}
                    </p>
                  </div>

                  <div className="mt-6 text-center">
                    <button
                      onClick={() => navigate('/signup')}
                      className="px-8 sm:px-10 py-3 sm:py-4 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-black rounded-xl hover:shadow-xl hover:shadow-yellow-500/50 transition-all cursor-pointer text-base sm:text-lg whitespace-nowrap"
                    >
                      <i className="ri-rocket-line mr-2"></i>
                      SIGN UP FOR UNLIMITED PREDICTIONS
                    </button>
                  </div>
                </div>
              )}

              {!isAnalyzing && !demoPrediction && (
                <div className="text-center py-12">
                  <i className="ri-brain-line text-6xl text-cyan-500/50 mb-4"></i>
                  <p className="text-gray-400 font-bold text-base sm:text-lg">Click above to see a real AI prediction</p>
                  <p className="text-gray-500 text-sm mt-2">Analyzes live game data for accurate results</p>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Native Ad Below AI Predictor */}
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 mb-8 sm:mb-10">
          <div className="flex justify-center my-6 sm:my-8 px-2">
            <SmartAdUnit 
              format="native"
              className="mx-auto"
            />
          </div>

          {/* In-Feed AdSense Ad */}
          <InFeedAdUnit className="my-6 sm:my-8" />
        </div>

        {/* Live Games Grid - Mobile Optimized */}
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-6 sm:mb-8">
            <h3 className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-2 px-2">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">LIVE CASINO GAMES</span>
            </h3>
            <p className="text-xs sm:text-sm md:text-base text-gray-400 font-bold px-4">Real-time data from top platforms • Updated every 500ms</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-10">
            {platforms.map((platform) => (
              <div key={platform.id} className="space-y-4">
                <div className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-xl p-4 sm:p-5 md:p-6 border-2 border-yellow-500/30 shadow-xl hover:border-yellow-500/60 transition-all">
                  <div className="flex items-center justify-between mb-4 sm:mb-5">
                    <h3 className="text-lg sm:text-xl md:text-2xl font-black text-white">{platform.name}</h3>
                    <span className="px-2.5 sm:px-3 py-1 sm:py-1.5 bg-gradient-to-r from-green-500 to-green-600 text-white text-xs sm:text-sm font-black rounded-full flex items-center space-x-1 whitespace-nowrap shadow-lg shadow-green-500/50">
                      <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full animate-pulse"></span>
                      <span>LIVE</span>
                    </span>
                  </div>
                  
                  {games.map((game) => {
                    const gameData = liveGames.find(
                      g => g.platform === platform.name && g.game === game.name
                    );
                    
                    return (
                      <div
                        key={game.id}
                        onClick={() => navigate(game.route)}
                        className="bg-black/50 rounded-lg p-3 sm:p-4 mb-3 cursor-pointer hover:bg-black/70 transition-all border-2 border-yellow-500/20 hover:border-yellow-500/60 hover:scale-105 hover:shadow-lg hover:shadow-yellow-500/20"
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-2">
                            <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-400 to-orange-600 rounded-lg flex items-center justify-center shadow-lg">
                              <i className={`${game.icon} text-xl sm:text-2xl text-black`}></i>
                            </div>
                            <span className="text-white font-black text-sm sm:text-base md:text-lg">{game.name}</span>
                          </div>
                          <span className={`px-2 sm:px-2.5 py-1 rounded-full text-xs sm:text-sm font-black border-2 ${
                            gameData?.status === 'flying' ? 'bg-green-500/20 text-green-400 border-green-500/50' : 
                            gameData?.status === 'crashed' ? 'bg-red-500/20 text-red-400 border-red-500/50' :
                            'bg-yellow-500/20 text-yellow-400 border-yellow-500/50'
                          }`}>
                            {gameData?.status === 'flying' ? '🚀 FLYING' : 
                             gameData?.status === 'crashed' ? '💥 CRASHED' : '⏳ WAITING'}
                          </span>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-2.5 sm:gap-3">
                          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-2 sm:p-2.5">
                            <p className="text-yellow-400 text-xs sm:text-sm font-bold mb-1">MULTIPLIER</p>
                            <p className="text-white font-black text-base sm:text-lg md:text-xl">{gameData?.currentMultiplier.toFixed(2) || '1.00'}x</p>
                          </div>
                          <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-2 sm:p-2.5">
                            <p className="text-blue-400 text-xs sm:text-sm font-bold mb-1">ROUND</p>
                            <p className="text-white font-black text-base sm:text-lg md:text-xl">{gameData?.roundId || '#0000'}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* Mid-Content Ad Banner */}
          <div className="flex justify-center my-8 sm:my-10 md:my-12 px-2">
            <AdUnit 
              network="google-adsense"
              format="banner"
              className="mx-auto"
            />
          </div>

          {/* In-Feed AdSense Ad */}
          <InFeedAdUnit className="my-6 sm:my-8" />

          {/* Features Section - Mobile Optimized */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 md:gap-6 mb-8 sm:mb-10 md:mb-12">
            {[
              { icon: 'ri-brain-line', title: 'AI-Powered', desc: 'Advanced algorithms analyze real-time casino data', color: 'from-purple-500 to-purple-700' },
              { icon: 'ri-shield-check-line', title: '100% Free', desc: 'Watch ads to unlock unlimited predictions', color: 'from-green-500 to-green-700' },
              { icon: 'ri-line-chart-line', title: '85%+ Accuracy', desc: 'Proven track record with high success rate', color: 'from-yellow-500 to-orange-600' }
            ].map((feature, index) => (
              <div key={index} className="bg-gradient-to-br from-gray-900 via-black to-gray-900 rounded-xl p-6 sm:p-7 md:p-8 border-2 border-yellow-500/30 text-center hover:border-yellow-500/60 transition-all hover:scale-105">
                <div className={`w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-gradient-to-br ${feature.color} rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4 shadow-xl border-2 border-white/20`}>
                  <i className={`${feature.icon} text-3xl sm:text-4xl text-white`}></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-black text-white mb-2">{feature.title}</h3>
                <p className="text-sm sm:text-base text-gray-400 font-semibold">{feature.desc}</p>
              </div>
            ))}
          </div>

          {/* Bottom Ad Banner */}
          <div className="flex justify-center my-8 sm:my-10 md:my-12 px-2">
            <SmartAdUnit 
              format="banner"
              className="mx-auto"
            />
          </div>

          {/* In-Feed AdSense Ad */}
          <InFeedAdUnit className="my-6 sm:my-8" />
        </div>
      </section>

      {/* Bottom Ad Banner */}
      <div className="py-6 sm:py-8 flex justify-center">
        <AdUnit 
          network="google-adsense"
          format="banner"
        />
      </div>

      {/* Footer - Mobile Optimized */}
      <footer className="relative bg-gradient-to-r from-black via-gray-900 to-black backdrop-blur-md border-t-2 border-yellow-500/30 py-6 sm:py-7 md:py-8">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex justify-center mb-6 sm:mb-7 md:mb-8 px-2">
            <SmartAdUnit 
              format="banner"
              className="mx-auto"
            />
          </div>
          
          {/* In-Feed AdSense Ad in Footer */}
          <InFeedAdUnit className="my-6 sm:my-8" />
          
          <div className="text-center px-4">
            <p className="text-gray-400 text-xs sm:text-sm md:text-base mb-2 font-bold">
              © 2025 Free PredictorPlay. All rights reserved.
            </p>
            <p className="text-yellow-500/80 text-xs sm:text-sm font-semibold">
              AI-powered predictions for entertainment purposes only. Gamble responsibly. 18+
            </p>
            <p className="text-yellow-400 text-xs sm:text-sm mt-2 font-bold">
              Powered by Free PredictorPlay 2025
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
